import moment from 'moment';
import {
  combo,
  pickBy,
  pick,
} from '@/pages/intelligence/components/DownloadCommon/DownloadPage/utils/picker';
import { toUpperFirstCase } from '@/pages/intelligence/utils/topchartsUtils';
import { getRrowthRate } from '@/pages/intelligence/utils/commonUtils';
import { getIfPercentage } from '@/pages/intelligence/utils/common';
import { getRegionCountryData, getFormatDate } from '../utils';
import { expandDataGenreProfileDetailStatsPlus } from '@/pages/intelligence/components/DownloadCommon/DownloadPage/common/genreProfileDetailStatsSetData';
import { expandDataGenreProfileTopChartsPlus } from '@/pages/intelligence/components/DownloadCommon/DownloadPage/common/genreProfileTopChartsSetData';
import { expandDataPcConsoleDetailSetDatasPlus } from '@/pages/intelligence/components/DownloadCommon/DownloadPage/common/pcConsoleDetailSetDatas';
// import { combo as combo2, pickBy as pickBy2, pick as pick2 } from '../utils/pickV2/picker';
import { getGameInfoById } from '@intelligence/services/common';
import { isEmpty } from '@intelligence/v3/utils/common';
import {
  isStoreRankMetricsRow,
  normalizeStoreRankMetricsRows,
  sortStoreRankMetricsRows,
} from '@intelligence/components/DownloadCommon/DownloadPage/configs/storeRankMetricsDownload';

const defaultDate = moment().format('YYYY-MM-DD');
const isEmptyObject = (obj: Record<string, any>) =>
  Object.keys(obj).length === 0;

const toPercentDownloadValue = (metric: string, value: any) => {
  const metricKey = metric.split('@#')[0] || '';
  const baseMetric = metricKey.includes('__')
    ? metricKey.split('__').pop() || metricKey
    : metricKey;
  if (
    metricKey.includes('meta_score') ||
    (!getIfPercentage(metricKey) &&
      !getIfPercentage(baseMetric) &&
      !metricKey.includes('players_by_country'))
  ) {
    return value;
  }
  if (value === '' || value == null) return value;
  const num = Number(value);
  return Number.isNaN(num) ? value : num * 100;
};
const setSplitStr = (str: string) => {
  const splitStr = str.split('@#');
  return {
    market: splitStr[0],
    platform: splitStr[1] || '',
    date: splitStr[2],
  };
};

// 舆情指标（Compare/Multi-dimension页面的舆情指标）
const OPINIONS_METRICS = [
  'Steam Added Positive%',
  'Steam Added Reviews',
  'Steam All reviews Positive%',
  'Steam All reviews',
  'Brand Health',
  'Mentions',
];

/**
 * 扩展数据函数，用于处理给定的列表和区域选项。
 *
 * @param list - 要处理的对象数组，每个对象包含一个 metric_value_list 属性。
 * @param regionOptions - 区域选项，用于获取国家和地区信息。
 * @returns 返回一个新的对象数组，其中包含处理后的数据。
 *
 * 该函数的工作流程如下：
 * 1. 遍历输入的列表，提取每个对象的 metric_value_list。
 * 2. 根据市场、区域、平台和日期对 metric_value_list 进行分组。
 * 3. 如果没有找到有效的分组，则将原始 metric_value_list 的值添加到新列表中。
 * 4. 如果找到有效的分组，则为每个分组创建一个新的对象，并将相关信息（如日期、国家和地区）添加到该对象中。
 */
const expandData = (
  downloadKey: string,
  list: any,
  regionOptions: any,
  params?: {
    b_group_by_region: boolean;
    b_group_by_market: boolean;
    b_group_by_device?: boolean;
    region_method: string;
  },
) => {
  const newList: any = [];
  list?.forEach((item: any) => {
    const { metric_value_list, ...other } = item;
    const cacheObject: any = {};
    metric_value_list?.forEach((subItem: any) => {
      /**
       * Compare/Multi-dimension页面下载
       * 由于后端接口在处理舆情的这几个指标时补齐了region、market（别的业务需要补齐）
       * 所以导致这里下载按date拆分时，出现同一date在表格中出现了多行数据的问题
       * 所以这里需要再不按地区、国家拆分时，删除接口返回的指标的region、market
       */
      if (OPINIONS_METRICS.includes(subItem?.metric)) {
        if (!params?.b_group_by_region) {
          subItem.region = '';
        }
        if (!params?.b_group_by_market) {
          subItem.market = '';
        }
      }

      if (downloadKey === 'pcConsoleGameNew') {
        // pcConsoleGameNew 下载场景：对所有指标统一处理
        if (!params?.b_group_by_region && !params?.b_group_by_market) {
          subItem.region = '';
          subItem.market = '';
        }
      }

      if (
        subItem?.market ||
        subItem?.region ||
        subItem?.platform ||
        subItem?.device ||
        subItem?.date
      ) {
        const mk = subItem?.region || subItem?.market;
        const isPcConsoleGameNewByDevice =
          downloadKey === 'pcConsoleGameNew' && params?.b_group_by_device;
        const subItemKey = isPcConsoleGameNewByDevice
          ? `${mk || ''}@#${subItem?.platform || ''}@#${subItem?.device || ''}@#${subItem?.date}`
          : `${mk || ''}@#${subItem?.platform || ''}@#${subItem?.date}`;
        if (!cacheObject[subItemKey]) {
          cacheObject[subItemKey] = [subItem];
        } else {
          cacheObject[subItemKey].push(subItem);
        }
      }
    });
    const subObject: any = {};
    if (isEmptyObject(cacheObject)) {
      const mvl = item.metric_value_list;
      mvl?.forEach((thirdItem: any) => {
        const defaultKey = `${thirdItem.metric}${thirdItem?.segment?.length ? `@#${thirdItem?.segment}` : ''
          }`;
        subObject[`${defaultKey}`] = thirdItem?.value;
        subObject[`${defaultKey}@#growth_ratio`] = thirdItem?.growth_ratio;
      });
      newList.push({
        ...other,
        ...subObject,
      });
    } else {
      for (const [key, value] of Object.entries(cacheObject)) {
        const isPcConsoleGameNewByDevice =
          downloadKey === 'pcConsoleGameNew' && params?.b_group_by_device;
        const splitKey = key.split('@#');
        const market = splitKey[0];
        const platform = splitKey[1] || '';
        const device = isPcConsoleGameNewByDevice ? splitKey[2] || '' : '';
        const date = splitKey[isPcConsoleGameNewByDevice ? 3 : 2];
        const subObject: any = {};
        // @ts-ignore
        value?.forEach((thirdItem: any) => {
          const defaultKey = `${thirdItem.metric}${thirdItem?.segment?.length ? `@#${thirdItem?.segment}` : ''
            }`;
          subObject[`${defaultKey}`] = thirdItem?.value;
          subObject[`${defaultKey}@#growth_ratio`] = thirdItem?.growth_ratio;
        });
        const countryAndRegion = getRegionCountryData({
          regionMethod: params?.region_method,
          options: regionOptions,
          code: market,
        });
        newList.push({
          ...other,
          ...subObject,
          date,
          ...countryAndRegion,
          platforms: platform,
          device,
        });
      }
    }
  });

  return newList;
};

// 优先取device
export const getStdPlatform = (
  platform?: string,
  device?: string,
): string | undefined => {
  return device || platform || '';
};

/**
 * 扩展数据函数，用于处理给定的列表和区域选项。
 *
 * @param list - 要处理的对象数组，每个对象包含一个 metric_value_list 属性。
 * @param regionOptions - 区域选项，用于获取国家和地区信息。
 * @returns 返回一个新的对象数组，其中包含处理后的数据。
 *
 * 该函数的工作流程如下：
 * 1. 遍历输入的列表，提取每个对象的 metric_value_list。
 * 2. 根据市场、区域、平台和日期对 metric_value_list 进行分组。
 * 3. 如果没有找到有效的分组，则将原始 metric_value_list 的值添加到新列表中。
 * 4. 如果找到有效的分组，则为每个分组创建一个新的对象，并将相关信息（如日期、国家和地区）添加到该对象中。
 */
// pcConsoleTopCharts榜单
const expandDataByPcConsoleTopCharts = (
  list: any,
  regionOptions: any,
  params: any = {},
) => {
  const newList: any = [];
  list?.forEach((item: any) => {
    const { metric_value_list, ...other } = item;
    const cacheObject: any = {};
    metric_value_list?.forEach((subItem: any) => {
      if (!subItem) return;
      if (
        subItem?.market ||
        subItem?.region ||
        subItem?.platform ||
        subItem?.device ||
        subItem?.date ||
        subItem?.id
      ) {
        const isByDevice = params?.device_method === 'device';
        const platform = isByDevice
          ? subItem?.platform || ''
          : getStdPlatform(subItem?.platform, subItem?.device);
        const device = isByDevice ? subItem?.device || '' : '';
        const isNoByFilter =
          params?.region_method === 'aggregated' &&
          params?.device_method === 'aggregated' &&
          params?.downlaod_date_method === 'aggregated';
        const mk =
          isNoByFilter ||
            (params.meta_score_show && params.region_method === 'aggregated')
            ? ''
            : subItem?.region || subItem?.market;
        const platformKey =
          params.meta_score_show && params.device_method === 'aggregated'
            ? ''
            : platform || '';
        const subItemKey = `${mk || ''}@#${platformKey}@#${device}@#${subItem?.date}`;
        if (!cacheObject[subItemKey]) {
          cacheObject[subItemKey] = [subItem];
        } else {
          cacheObject[subItemKey].push(subItem);
        }
      }
    });
    const subObject: any = {};
    if (isEmptyObject(cacheObject)) {
      const mvl = item.metric_value_list;
      mvl?.forEach((thirdItem: any) => {
        const defaultKey = `${thirdItem.metric}`;
        subObject[`${defaultKey}`] = toPercentDownloadValue(
          defaultKey,
          thirdItem?.value,
        );
        subObject[`${defaultKey}@#growth_ratio`] = thirdItem?.growth_ratio;
      });
      newList.push({
        ...other,
        ...subObject,
      });
    } else {
      for (const [key, value] of Object.entries(cacheObject)) {
        const [market, platform, device, date] = key.split('@#');
        const subObject: any = {};
        // @ts-ignore
        value?.forEach((thirdItem: any) => {
          const defaultKey = `${thirdItem.metric}`;
          const metricKey = `${defaultKey}${params.meta_score_show &&
            defaultKey === 'meta_score' &&
            params.device_method === 'aggregated'
            ? `_${thirdItem.platform}`
            : ''
            }`;
          subObject[metricKey] = toPercentDownloadValue(
            metricKey,
            thirdItem?.value,
          );
          subObject[`${defaultKey}@#growth_ratio`] = thirdItem?.growth_ratio;
        });
        const countryAndRegion = getRegionCountryData({
          regionMethod: params?.region_method,
          options: regionOptions,
          code: market,
        });
        // console.log('countryAndRegion', countryAndRegion);
        newList.push({
          ...other,
          ...subObject,
          date,
          ...countryAndRegion,
          platforms: platform,
          device,
        });
      }
    }
  });
  // console.log('newListrefnewList', newList);
  return newList;
};

/**
 * 扩展数据函数，用于处理给定的列表和区域选项。-------通用处理函数
 *
 * @param list - 要处理的对象数组，每个对象包含一个 metric_value_list 属性。
 * @param regionOptions - 区域选项，用于获取国家和地区信息。
 * @returns 返回一个新的对象数组，其中包含处理后的数据。
 *
 * 该函数的工作流程如下：
 * 1. 遍历输入的列表，提取每个对象的 metric_value_list。
 * 2. 根据市场、区域、平台和日期对 metric_value_list 进行分组。
 * 3. 如果没有找到有效的分组，则将原始 metric_value_list 的值添加到新列表中。
 * 4. 如果找到有效的分组，则为每个分组创建一个新的对象，并将相关信息（如日期、国家和地区）添加到该对象中。
 */
// pcConsoleTopCharts榜单
const expandDataCommon = (
  list: any,
  regionOptions: any,
  params: any = {},
  isNeedCountRate = false,
  isNeedPre = true,
) => {
  // console.debug('expandDataCommon-list', list);
  // console.debug('expandDataCommon-regionOptions', regionOptions);
  // console.debug('expandDataCommon-params', params);
  const dateTye = params?.granularity;
  const listTemp = list?.metric_value_list || [];
  const newList: any = [];
  const cacheObject: any = {};
  listTemp?.forEach((subItem: any) => {
    if (!subItem) return;

    if (subItem?.date || subItem?.market || subItem?.region || subItem?.genre) {
      const isNoByFilter =
        params?.region_method === 'aggregated' &&
        params?.device_method === 'aggregated' &&
        params?.downlaod_date_method === 'aggregated';
      const mk = isNoByFilter ? '' : subItem?.region || subItem?.market;
      const subItemKey = `${mk || ''}@#${subItem?.genre || ''}@#${subItem?.date
        }`;
      if (!cacheObject[subItemKey]) {
        cacheObject[subItemKey] = [subItem];
      } else {
        cacheObject[subItemKey].push(subItem);
      }
    }
  });
  const subObject: any = {};
  if (isEmptyObject(cacheObject)) {
    const mvl = listTemp;
    mvl?.forEach((thirdItem: any) => {
      const defaultKey = `${thirdItem.metric}`;
      subObject[`${defaultKey}`] = thirdItem?.value;
      subObject[`${defaultKey}@#growth_ratio`] = isNeedCountRate
        ? getRrowthRate(thirdItem?.value, thirdItem?.value_ratio, isNeedPre)
        : thirdItem?.growth_ratio;
    });
    newList.push({
      ...subObject,
    });
  } else {
    for (const [key, value] of Object.entries(cacheObject)) {
      const { market, platform, date } = setSplitStr(key);
      const subObject: any = {};
      // @ts-ignore
      value?.forEach((thirdItem: any) => {
        const defaultKey = `${thirdItem.metric}`;
        subObject[`${defaultKey}`] = thirdItem?.value;
        subObject[`${defaultKey}@#growth_ratio`] = isNeedCountRate
          ? getRrowthRate(thirdItem?.value, thirdItem?.value_ratio, isNeedPre)
          : thirdItem?.growth_ratio;
      });
      const countryAndRegion = getRegionCountryData({
        regionMethod: params?.region_method,
        options: regionOptions,
        code: market,
      });
      newList.push({
        ...subObject,
        date: getFormatDate(date, dateTye),
        ...countryAndRegion,
        genre: platform,
      });
    }
  }
  // console.debug('newListrefnewList', newList);
  return newList;
};

// marketProfileStats-Operation Data
const expandDataMarketProfileStatsOperationData = (
  list: any,
  regionOptions: any,
  params: any = {},
) => {
  // console.log('expandDataCommon-list', list);
  // console.log('expandDataCommon-regionOptions', regionOptions);
  // console.log('expandDataCommon-params', params);
  const dataSource = params?.filter?.dataSource || 'sensortower';
  const listTemp = list?.metric_value_list || [];
  const newList: any = [];

  const cacheObject: any = {};
  listTemp?.forEach((subItem: any) => {
    if (!subItem) return;
    if (subItem?.date || subItem?.genre) {
      const isNoByFilter =
        params?.region_method === 'aggregated' &&
        params?.device_method === 'aggregated' &&
        params?.downlaod_date_method === 'aggregated';
      const mk = isNoByFilter ? '' : subItem?.region || subItem?.market;
      const subItemKey = `${mk || ''}@#${subItem?.genre || ''}@#${subItem?.date
        }`;
      if (!cacheObject[subItemKey]) {
        cacheObject[subItemKey] = [subItem];
      } else {
        cacheObject[subItemKey].push(subItem);
      }
    }
  });
  const subObject: any = {};
  if (isEmptyObject(cacheObject)) {
    const mvl = listTemp;
    // let otherObj = {};
    mvl?.forEach((thirdItem: any) => {
      // otherObj = thirdItem;
      const defaultKey = `${toUpperFirstCase(thirdItem.metric)}(${dataSource})`;
      subObject[`${defaultKey}`] = thirdItem?.value;
      subObject[`${defaultKey}@#growth_ratio`] = getRrowthRate(
        thirdItem?.value,
        thirdItem?.value_ratio,
      );
    });
    newList.push({
      // ...otherObj,
      ...subObject,
    });
  } else {
    for (const [key, value] of Object.entries(cacheObject)) {
      const { market, platform, date } = setSplitStr(key);
      const subObject: any = {};
      // @ts-ignore
      value?.forEach((thirdItem: any) => {
        const defaultKey = `${toUpperFirstCase(
          thirdItem.metric,
        )}(${dataSource})`;
        subObject[`${defaultKey}`] = thirdItem?.value;
        subObject[`${defaultKey}@#growth_ratio`] = getRrowthRate(
          thirdItem?.value,
          thirdItem?.value_ratio,
        );
      });
      const countryAndRegion = getRegionCountryData({
        regionMethod: params?.region_method,
        options: regionOptions,
        code: market,
      });
      // console.log('countryAndRegion', countryAndRegion);
      newList.push({
        // ...other,
        ...subObject,
        date,
        ...countryAndRegion,
        genre: platform,
      });
    }
  }

  // console.log('newListrefnewList', newList);
  return newList;
};
// marketProfileStats-Game Data
const expandDataMarketProfileStatsGameData = (
  list: any,
  regionOptions: any,
  params: any = {},
) => {
  // console.log('expandDataCommon-list1212', list);
  // console.log('expandDataCommon-regionOptions1212', regionOptions);
  // console.log('expandDataCommon-params1212', params);
  const dataSource = params?.filter?.dataSource || 'sensortower';
  const dateTye = params?.filter?.granularity || 'monthly';
  const listTemp = list || [];
  const newList: any = [];
  listTemp?.forEach((item: any) => {
    const { metric_value_list = [], ...other } = item;
    const cacheObject: any = {};
    metric_value_list?.forEach((subItem: any) => {
      if (!subItem) return;

      if (subItem?.date) {
        const isNoByFilter =
          params?.region_method === 'aggregated' &&
          params?.device_method === 'aggregated' &&
          params?.downlaod_date_method === 'aggregated';
        const mk = isNoByFilter ? '' : subItem?.region || subItem?.market;
        const subItemKey = `${mk || ''}@#${subItem?.device || ''}@#${subItem?.date
          }`;
        if (!cacheObject[subItemKey]) {
          cacheObject[subItemKey] = [subItem];
        } else {
          cacheObject[subItemKey].push(subItem);
        }
      }
    });
    const subObject: any = {};
    if (isEmptyObject(cacheObject)) {
      const mvl = metric_value_list;
      // 如果不是bydate
      // if (params.downlaod_date_method !== 'aggregated') {
      mvl?.forEach((thirdItem: any) => {
        const defaultKey = `${toUpperFirstCase(
          thirdItem.metric === 'dau' ? 'mau' : thirdItem.metric,
        )}(${dataSource})`;
        subObject[`${defaultKey}`] = thirdItem?.value;
        subObject[`${defaultKey}@#growth_ratio`] =
          thirdItem?.growth_ratio ||
          getRrowthRate(thirdItem?.value, thirdItem?.value_ratio);
      });
      newList.push({
        ...other,
        ...subObject,
      });
    } else {
      for (const [key, value] of Object.entries(cacheObject)) {
        const { market, platform, date } = setSplitStr(key);
        const subObject: any = {};
        // @ts-ignore
        value?.forEach((thirdItem: any) => {
          const defaultKey = `${toUpperFirstCase(
            thirdItem.metric === 'dau' ? 'mau' : thirdItem.metric,
          )}(${dataSource})`;
          subObject[`${defaultKey}`] = thirdItem?.value;
          subObject[`${defaultKey}@#growth_ratio`] =
            thirdItem?.growth_ratio ||
            getRrowthRate(thirdItem?.value, thirdItem?.value_ratio);
        });
        const countryAndRegion = getRegionCountryData({
          regionMethod: params?.region_method,
          options: regionOptions,
          code: market,
        });
        // console.log('countryAndRegion', countryAndRegion);
        // console.log('subObjectsubObjectsubObject', subObject);
        newList.push({
          ...other,
          ...subObject,
          date,
          ...countryAndRegion,
          platforms: platform,
        });
      }
    }
  });
  // console.log('newListrefnewList', newList);
  return newList;
};
// marketProfileStats-commpany
const expandDataMarketProfileStatsCommpanyData = (
  list: any,
  regionOptions: any,
  params: any = {},
) => {
  // console.log('expandDataCommon-list1212', list);
  // console.log('expandDataCommon-regionOptions1212', regionOptions);
  // console.log('expandDataCommon-params1212', params);

  const dataSource = params?.filter?.dataSource || 'sensortower';

  const listTemp = list || [];
  const newList: any = [];
  listTemp?.forEach((item: any, indexPa: number) => {
    const { metric_value_list = [], ...other } = item;
    const cacheObject: any = {};
    metric_value_list?.forEach((subItem: any) => {
      if (!subItem) return;

      if (subItem?.date) {
        const isNoByFilter =
          params?.region_method === 'aggregated' &&
          params?.device_method === 'aggregated' &&
          params?.downlaod_date_method === 'aggregated';
        const mk = isNoByFilter ? '' : subItem?.region || subItem?.market;
        const subItemKey = `${mk || ''}@#${subItem?.device || ''}@#${subItem?.date
          }`;
        if (!cacheObject[subItemKey]) {
          cacheObject[subItemKey] = [subItem];
        } else {
          cacheObject[subItemKey].push(subItem);
        }
      }
    });
    const subObject: any = {};
    if (isEmptyObject(cacheObject)) {
      const mvl = metric_value_list;
      // 如果不是bydate
      // if (params.downlaod_date_method !== 'aggregated') {
      mvl?.forEach((thirdItem: any) => {
        const defaultKey = `${toUpperFirstCase(
          thirdItem.metric,
        )}(${dataSource})`;
        subObject[`${defaultKey}`] = thirdItem?.value;
        subObject[`${defaultKey}@#growth_ratio`] =
          thirdItem?.growth_ratio ||
          getRrowthRate(thirdItem?.value, thirdItem?.value_ratio);
      });
      newList.push({
        ...other,
        ...subObject,
      });
    } else {
      // console.log('cacheObjectcacheObject655656', cacheObject);
      for (const [key, value] of Object.entries(cacheObject)) {
        const { market, platform, date } = setSplitStr(key);
        const subObject: any = {};
        // @ts-ignore
        value?.forEach((thirdItem: any) => {
          const defaultKey = `${toUpperFirstCase(
            thirdItem.metric,
          )}(${dataSource})`;
          subObject[`${defaultKey}`] = thirdItem?.value;
          subObject[`${defaultKey}@#growth_ratio`] =
            thirdItem?.growth_ratio ||
            getRrowthRate(thirdItem?.value, thirdItem?.value_ratio);
          // subObject.rank = Number(indexPa) + 1;
        });
        const countryAndRegion = getRegionCountryData({
          regionMethod: params?.region_method,
          options: regionOptions,
          code: market,
        });
        newList.push({
          ...other,
          ...subObject,
          date,
          ...countryAndRegion,
          platforms: platform,
        });
      }
    }
  });
  // console.log('newListrefnewList', newList);
  return newList;
};

const sortDataByDateAndId = function (
  data: any[],
  byDate?: boolean,
  isRelease?: boolean,
  granularity?: boolean,
  countryDevice?: boolean,
  revenue?: boolean,
  byPlatform?: boolean,
) {
  if (byPlatform) {
    const sortedGames = [...data].sort((a: any, b: any) => {
      // 第一优先级：按 gameId 升序
      if (a.id !== b.id) return a.id - b.id;
      // 第二优先级：按 time 降序（新时间在前）
      if (b?.date !== a?.date) return moment(b?.date).diff(moment(a?.date));
      const mapOrder: any = {
        Aggregated: 0,
        PC: 1,
        PlayStation: 2,
        'PlayStation 4': 3,
        'PlayStation 5': 4,
        Xbox: 5,
        'Xbox One': 6,
        'Xbox Series X|S': 7,
        Nintendo: 8,
        'Nintendo Switch': 9,
        'Nintendo Will U': 10,
      };
      const deviceCompares = mapOrder[a?.platforms] - mapOrder[b?.platforms];
      if (deviceCompares !== 0) return deviceCompares;
    });
    return sortedGames;
  }
  if (!byDate) return data;

  // pcconsole榜单，特殊排序
  if (!countryDevice) {
    const sortedGames = [...data].sort((a, b) => {
      // 第一优先级：按 gameId 升序
      if (a.id !== b.id) return a.id - b.id;
      // 第二优先级：按 time 降序（新时间在前）
      if (b.date !== a.date)
        return isRelease
          ? Number.parseInt(b?.date, 10) - Number.parseInt(a?.date, 10)
          : moment(b?.date).diff(moment(a?.date));
      // 第三优先级: 国家字母升序
      const deviceCompare = a?.country_english?.localeCompare(
        b?.country_english,
      );
      if (deviceCompare !== 0) return deviceCompare;
      // 第四优先级：按 device 字母升序（A-Z）
      return a?.platforms?.localeCompare(b?.platforms);
    });
    return sortedGames;
  }

  // pcconsole榜单，特殊排序
  if (!revenue) {
    const sortedGames = [...data].sort((a: any, b: any) => {
      // 第一优先级：按 gameId 升序
      if (a.id !== b.id) return a.id - b.id;
      // 第二优先级：按 time 降序（新时间在前）
      if (b.date !== a.date) return moment(b?.date).diff(moment(a?.date));
      if (a.revenue !== b.revenue) return a.revenue - b.revenue;
    });
    return sortedGames;
  }

  // 首先按照id分组
  const groupedData = data.reduce((acc: any, item: any) => {
    if (!acc[item.id]) {
      acc[item.id] = [];
    }
    acc[item.id].push(item);
    return acc;
  }, {});

  let sortedData = [];

  if (!isRelease) {
    // 然后对每个分组按照date降序排序
    sortedData = Object.keys(groupedData).reduce((acc: any, id: any) => {
      acc.push(
        ...groupedData[id].sort((a: any, b: any) =>
          moment(b.date).diff(moment(a.date)),
        ),
      );
      return acc;
    }, []);
  } else {
    // 然后对每个分组按照date升序排序
    sortedData = Object.keys(groupedData).reduce((acc: any, id: any) => {
      acc.push(...groupedData[id].sort((a: any, b: any) => b.date - a.date));
      return acc;
    }, []);
    sortedData = sortedData.map((ele: any) => ({
      ...ele,
      date: ele.date ? `${granularity}${ele.date}` : undefined,
    }));
  }

  return sortedData;
};

const tileByKeyword = (data: any) => {
  const list: any = [];
  if (!Array.isArray(data)) {
    return list;
  }
  data.forEach((item: any) => {
    const maps: any = {};
    const element = item.source_metric_list || [];
    element.forEach((elementSub: any) => {
      if (maps[elementSub.date]) {
        maps[elementSub.date].push(elementSub);
      } else {
        maps[elementSub.date] = [elementSub];
      }
    });
    Object.values(maps).forEach(mapItem => {
      list.push({
        ...item,
        source_metric_list: mapItem,
      });
    });
  });
  return list;
};
const tileArray = (
  data: any,
  tileKey: string,
  insideKey: string,
  insideValue: string,
  params?: any,
) => {
  const list: any = [];
  if (!Array.isArray(data)) {
    return list;
  }
  data?.forEach((item: Record<string, any>) => {
    const element = item[tileKey] || [];
    item.date = element[0]?.date || defaultDate;
    element.forEach((innerItem: Record<string, any>) => {
      const innerItemKey = `${innerItem.data_source}|${innerItem.metric}`;
      item[innerItemKey] = innerItem[insideValue];
      item[`${innerItemKey}_change`] = innerItem.growth_ratio;
      if (params?.genre_source === 'iegg') {
        item.mainGenre = item.iegg_genre;
        item.genre_source = item.iegg_sub_genre;
      } else {
        item.mainGenre = item.genre;
        item.genre_source = item.sub_genre;
      }
    });
    list.push(item);
  });
  return list;
};

const getMobileStoreRankMoreRank = (rank: any) => {
  const rankValue = Number(rank);
  if (Number.isFinite(rankValue)) {
    return rankValue;
  }
  return Number.POSITIVE_INFINITY;
};

const sortMobileStoreRankMoreRows = (data: any[]) => {
  if (!Array.isArray(data)) {
    return [];
  }

  return [...data].sort((prev, next) => {
    const dateDiff = moment(next?.date).diff(moment(prev?.date));
    if (dateDiff !== 0) {
      return dateDiff;
    }
    return getMobileStoreRankMoreRank(prev?.rank) - getMobileStoreRankMoreRank(next?.rank);
  });
};

const isMobileStoreRankMoreFlatRow = (item: Record<string, any>) => {
  if (!item || Array.isArray(item?.source_metric_list)) {
    return false;
  }

  const itemKeys = Object.keys(item);
  const hasBasicFields =
    Reflect.has(item, 'date') &&
    Reflect.has(item, 'rank') &&
    Reflect.has(item, 'name');
  const hasMetricField = itemKeys.some((key) => key.includes('|'));

  return hasBasicFields && hasMetricField;
};

const normalizeMobileStoreRankMoreFlatRows = (
  data: Record<string, any>[],
  params?: any,
) => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((item) => {
    const row = {
      ...item,
    };

    Object.keys(item).forEach((key) => {
      if (!key.endsWith('_ratio')) {
        return;
      }
      const metricKey = key.replace(/_ratio$/, '');
      const ratioValue = item[key];

      if (isEmpty(ratioValue)) {
        row[`${metricKey}_change`] = ratioValue;
        return;
      }

      const ratioNumber = Number(ratioValue);
      if (Number.isNaN(ratioNumber)) {
        row[`${metricKey}_change`] = ratioValue;
        return;
      }

      row[`${metricKey}_change`] = ratioNumber;
    });

    if (params?.genre_source === 'iegg') {
      row.mainGenre = item.iegg_genre;
      row.genre_source = item.iegg_sub_genre;
    } else {
      row.mainGenre = item.genre;
      row.genre_source = item.sub_genre;
    }

    return row;
  });
};

const pcConsoleMarketCountExpandData = (
  allData: any,
  current: any,
  data: any,
) => {
  const { params = {} } = current;
  const { regionOptions = [] } = params;
  const list: any[] = [];
  data?.forEach((element: any) => {
    const { time_line: timeLine, basic_info, market } = element || {};
    const { company = [] } = basic_info;
    const countryAndRegion = getRegionCountryData({
      regionMethod: params?.region_method,
      options: regionOptions,
      code: market,
    });
    timeLine?.forEach((timeElement: any) => {
      const { metrics = [], time = '' } = timeElement;
      let itemData = {};
      metrics?.forEach((metricElement: any) => {
        const name = metricElement?.metric;
        itemData = {
          [name]: metricElement?.value,
          [`${name}_growth_ratio`]: metricElement?.growth_ratio,
        };
      });
      itemData = {
        ...itemData,
        ...timeElement,
        ...countryAndRegion,
        publisher: company[0]?.name,
        publisher_addr_full_name: company[0]?.country_abbre,
        parent_publisher: company[0]?.country_name,
        parent_developer: basic_info?.parent_publisher,
        release_time: basic_info?.release_time,
        steam_id: basic_info?.SteamID,
        iegg_genre: basic_info?.genre,
        iegg_sub_genre: basic_info?.sub_genre,
        date: time,
      };
      list.push(itemData);
    });
  });
  // return list;

  const mapping = {
    publisher: 'basic_info company name:first',
    publisher_addr_full_name: 'basic_info company country_abbre:first',
    parent_publisher: 'basic_info company country_name:first',
    parent_developer: 'basic_info parent_publisher:first',
    iegg_genre: 'basic_info genre:first',
    iegg_sub_genre: 'basic_info sub_genre:first',
    release_time: 'basic_info release_time:first',
    steam_id: 'basic_info SteamID:first',
    rank_1_3: 'market_count [date=@date] [ranking="1-3"] quantity',
    rank_1_3_dod: 'market_count [date=@date] [ranking="1-3"] dod',
    rank_4_10: 'market_count [date=@date] [ranking="4-10"] quantity',
    rank_4_10_dod: 'market_count [date=@date] [ranking="4-10"] dod',
    rank_11_50: 'market_count [date=@date] [ranking="11-50"] quantity',
    rank_11_50_dod: 'market_count [date=@date] [ranking="11-50"] dod',
    rank_51_200: 'market_count [date=@date] [ranking="51-200"] quantity',
    rank_51_200_dod: 'market_count [date=@date] [ranking="51-200"] dod',
  };

  const result = pickBy(allData, {
    date: '@date',
    '...info': () => {
      const obj = {};
      for (const item of current['Market Count'] || []) {
        if (mapping[item.key]) {
          obj[item.key] = mapping[item.key];
        }
      }
      return obj;
    },

    $key: () =>
      combo(pick(allData, 'market_count date')).map((e: any) => ({
        date: e[0],
      })),
  });
  return result;
};

const pcConsoleRankTableExpandData = (data: any, current: any) => {
  const { params = {} } = current;
  const { regionOptions = [] } = params;
  const list: any[] = [];
  data?.forEach((item: any) => {
    const { metrics = [], basic_info = {}, market } = item;
    const {
      company = [],
      parent_publisher,
      release_time,
      SteamID,
      genre,
      sub_genre,
    } = basic_info;
    const countryAndRegion = getRegionCountryData({
      regionMethod: params?.region_method,
      options: regionOptions,
      code: market,
    });
    const newItem = {
      ...item,
      ...countryAndRegion,
      iegg_sub_genre: sub_genre,
      iegg_genre: genre,
      steam_id: SteamID,
      release_time,
      parent_developer: parent_publisher,
      publisher: company[0]?.name,
      publisher_addr_full_name: company[0]?.country_abbre,
      parent_publisher: company[0]?.country_name,
      order_source: '',
    };

    metrics?.forEach((subItem: any) => {
      const name = subItem?.metric;
      newItem[name] = subItem?.value;
      newItem[`${name}_growth_ratio`] = subItem?.dod;
    });
    list.push(newItem);
  });
  return list;
};
/**
 * 处理并扩展PCconsole排名趋势数据。
 *
 * @param data - 输入的数据数组，包含多个元素，每个元素代表一个时间线数据。
 * @param current - 当前上下文对象，包含参数信息。
 * @param current.params - 包含区域选项的对象。
 * @param current.params.regionOptions - 区域选项数组，用于获取国家和地区信息。
 * @returns 返回一个扩展后的数据列表，每个元素包含时间线、指标、公司信息等。
 */
const pcConsoleRankTrendExpandData = (data: any, current: any) => {
  const { params = {} } = current;
  const { regionOptions = [] } = params;
  const list: any[] = [];
  data?.forEach((element: any) => {
    const { time_line: timeLine, basic_info, market } = element || {};
    const { company = [] } = basic_info;
    const countryAndRegion = getRegionCountryData({
      regionMethod: params?.region_method,
      options: regionOptions,
      code: market,
    });
    timeLine?.reverse()?.forEach((timeElement: any) => {
      const { metrics = [], time = '' } = timeElement;
      let itemData = {};
      metrics?.forEach((metricElement: any) => {
        const name = metricElement?.metric;
        itemData = {
          ...itemData,
          [name]: metricElement?.value,
          [`${name}_growth_ratio`]: metricElement?.dod,
        };
      });
      itemData = {
        ...itemData,
        ...timeElement,
        ...countryAndRegion,
        publisher: company[0]?.name,
        publisher_addr_full_name: company[0]?.country_abbre,
        parent_publisher: company[0]?.country_name,
        parent_developer: basic_info?.parent_publisher,
        release_time: basic_info?.release_time,
        steam_id: basic_info?.SteamID,
        iegg_genre: basic_info?.genre,
        iegg_sub_genre: basic_info?.sub_genre,
        date: time,
        order_source: '',
      };
      list.push(itemData);
    });
  });
  return list;
};
/**
 * 根据下载键分析数据并返回处理后的数据列表。
 *
 * @param data - 需要分析的数据，类型为任意。
 * @param current - 当前上下文对象，包含区域选项和其他配置。
 * @param downloadKey - 用于确定数据处理方式的下载键。
 * @param allData - 所有可用的数据，类型为任意。
 * @returns 处理后的数据列表，类型为任意。
 *
 * @throws {Error} 如果提供的下载键不匹配任何已知的处理方式，将返回原始数据。
 */
export const analyseDataByDownloadKey = (
  data: any,
  current: any,
  downloadKey: string,
  allData: any,
  params?: any,
) => {
  const regionOptionsValue = current?.regionOptions || [];
  // console.log('analyseDataByDownloadKey-downloadKey', downloadKey);
  // console.log('analyseDataByDownloadKey-params', params);
  let newListValue: any = [];
  switch (downloadKey) {
    case 'pcConsoleGameNew': {
      // eslint-disable-next-line no-case-declarations
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.list)
            ? data.list
            : [];
      let newList = expandData(downloadKey, list, regionOptionsValue, params);
      newList = sortDataByDateAndId(
        newList,
        current?.byDate,
        current?.isRelease,
        current?.granularity,
      );
      // console.log('analyseDataByDownloadKey-newList', newList);
      return [newList, 'NONE'];
    }

    case 'pcConsoleTopCharts':
    case 'databrainCalibrationPCConsoleTopCharts':
      // console.log('datadatadata1231', data);
      // eslint-disable-next-line no-case-declarations
      newListValue = expandDataByPcConsoleTopCharts(
        data,
        regionOptionsValue,
        params,
      );
      // eslint-disable-next-line no-case-declarations
      const byPlatform = true;
      // console.log('newListValuenewListValue', newListValue);
      newListValue = sortDataByDateAndId(
        newListValue,
        current?.byDate,
        current?.isRelease,
        current?.granularity,
        current?.countryDevice,
        false,
        byPlatform,
      );
      return [newListValue, 'NONE'];
    case 'mobileStoreRankMore':
    case 'consoleStoreRankMore':
      if (Array.isArray(data) && data.some(isMobileStoreRankMoreFlatRow)) {
        return [
          sortMobileStoreRankMoreRows(
            normalizeMobileStoreRankMoreFlatRows(data, params),
          ),
          'NONE',
        ];
      }
      // eslint-disable-next-line no-case-declarations
      const list = tileByKeyword(data);
      return [
        sortMobileStoreRankMoreRows(
          tileArray(list, 'source_metric_list', 'metric', 'value', params),
        ),
        'NONE',
      ];
    case 'mobileStoreRankTop':
    case 'consoleStoreRankTop':
      if (Array.isArray(data) && data.some(isStoreRankMetricsRow)) {
        const marketInfo =
          allData?.ext?.market_info || params?.market_info || {};
        const entityInfo =
          allData?.ext?.entity_info || params?.entity_info || {};
        const normalizedParams = {
          ...params,
          regionOptions: regionOptionsValue,
          market_info: marketInfo,
          entity_info: entityInfo,
        };
        return [
          sortStoreRankMetricsRows(
            normalizeStoreRankMetricsRows(data, normalizedParams),
            normalizedParams,
          ),
          'NONE',
        ];
      }
      return [[], 'NONE'];
    case 'pcConsoleMarketCount':
      return [pcConsoleMarketCountExpandData(allData, current, data), 'NONE'];
    case 'pcConsoleRankTable-Rank Trend':
      return [pcConsoleRankTrendExpandData(data, current), 'NONE'];
    case 'pcConsoleRankTable-Rank Table':
      return [pcConsoleRankTableExpandData(data, current), 'NONE'];
    case 'mobileMarketProfileTopCharts':
      newListValue = expandDataCommon(
        data,
        regionOptionsValue,
        params,
        true,
        false,
      );
      newListValue = sortDataByDateAndId(newListValue, current?.byDate);
      return [newListValue, 'NONE'];
    case 'marketProfileStats-Operation Data':
      newListValue = expandDataMarketProfileStatsOperationData(
        data,
        regionOptionsValue,
        params,
      );
      return [newListValue, 'NONE'];
    case 'marketProfileStats-Game Data':
      newListValue = expandDataMarketProfileStatsGameData(
        data,
        regionOptionsValue,
        params,
      );
      // newListValue = sortDataByDateAndId(newListValue, current?.revenue);
      return [newListValue, 'NONE'];
    case 'marketProfileStats-Operation Data':
    case 'marketProfileStats-Main Genre Data':
      newListValue = expandDataMarketProfileStatsOperationData(
        data,
        regionOptionsValue,
        params,
      );
      newListValue = sortDataByDateAndId(newListValue, current?.revenue);
      return [newListValue, 'NONE'];
    case 'marketProfileStats-Companies':
      newListValue = expandDataMarketProfileStatsCommpanyData(
        data,
        regionOptionsValue,
        params,
      );
      // console.log('Companies-newListValue', newListValue);
      // newListValue = sortDataByDateAndSource(newListValue, params);
      return [newListValue, 'NONE'];
    case 'genreProfileDetailStats-Operation Data':
    case 'genreProfileDetailStats-Game Data':
    case 'genreProfileDetailStats-Country':
    case 'genreProfileDetailStats-Company':
      newListValue = expandDataGenreProfileDetailStatsPlus(
        data,
        params,
        downloadKey,
      );
      return [newListValue, 'DIRECT'];
    case 'genreProfileTopCharts-Main Genre':
    case 'genreProfileTopCharts-Sub Genre':
    case 'genreProfileTopCharts-Main+Sub Genre':
      newListValue = expandDataGenreProfileTopChartsPlus(
        data,
        params,
        regionOptionsValue,
        downloadKey,
      );
      return [newListValue, 'DIRECT'];
    case 'pcConsoleDetail':
      newListValue = expandDataPcConsoleDetailSetDatasPlus(
        data,
        params,
        regionOptionsValue,
        downloadKey,
      );
      return [newListValue, 'DIRECT'];
    case 'steamNews': {
      return [data, 'NONE'];
    }
    default:
      return data;
  }
};
